import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function Proven() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const metrics = [{
    value: isFR ? '88\u202f266' : '88,266',
    label: isFR ? 'Identités numériques vérifiées' : 'Verified digital identities'
  }, {
    value: isFR ? '75\u202f329' : '75,329',
    label: isFR ? 'Cartes citoyennes mobiles' : 'Citizen mobile cards'
  }, {
    value: '12+',
    label: isFR ? 'Services connectés' : 'Connected services'
  }, {
    value: '20',
    label: isFR ? 'Municipalités connectées' : 'Municipalities connected'
  }, {
    value: '~78\u202f%',
    label: isFR ? 'Couverture population (Brossard)' : 'Population coverage (Brossard)'
  }, {
    value: isFR ? 'Depuis 2017' : 'Since 2017',
    label: isFR ? 'En production' : 'Running in production'
  }];
  return <section id="proof" className="bg-white border-t border-[#E4E6EC] py-28 lg:py-36 px-6" data-id="element-605">
      {/* Header */}
      <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-606">
        <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-607">
          {isFR ? 'Éprouvé en production' : 'Proven in production'}
        </motion.div>
        <motion.h2 {...reveal} transition={{
        duration: 0.7,
        delay: 0.05
      }} className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] text-[#121212] leading-[1.12] tracking-tight mb-6" data-id="element-608">
          {isFR ? <>
              Adoptée par les municipalités.
              <br data-id="element-609" />
              En production depuis 2017.
            </> : <>
              Trusted by municipalities.
              <br data-id="element-610" />
              Running since 2017.
            </>}
        </motion.h2>
        <motion.p {...reveal} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-sans text-lg md:text-xl text-[#121212]/60 leading-relaxed max-w-2xl mx-auto" data-id="element-611">
          {isFR ? "BCITI n'est pas un prototype. La plateforme fait fonctionner l'identité numérique vérifiée, les communications, les services municipaux et une IA de confiance — en production." : 'BCITI is not a prototype. It powers verified digital identity, communications, municipal services and trusted AI in production.'}
        </motion.p>
      </div>

      {/* Results — production outcomes (the WOW) */}
      <motion.div {...reveal} transition={{
      duration: 0.7,
      delay: 0.12
    }} className="max-w-4xl mx-auto mb-14" data-id="element-612">
        <div className="text-center font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#4F46E5] mb-8" data-id="element-613">
          {isFR ? 'Résultats en production' : 'Production results'}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border border-[#E4E6EC] divide-y md:divide-y-0 md:divide-x divide-[#E4E6EC]" data-id="element-614">
          <div className="p-8 md:p-12 text-center" data-id="element-615">
            <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-616">
              400 <span className="text-[#4F46E5]" data-id="element-617">→</span> 5
            </div>
            <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-xs mx-auto" data-id="element-618">
              {isFR ? 'Personnes en attente au comptoir pour un renouvellement de carte numérique' : 'People waiting in line for a digital card renewal'}
            </div>
          </div>
          <div className="p-8 md:p-12 text-center" data-id="element-619">
            <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-620">
              {isFR ? '130\u202f000' : '130,000'}
              <span className="text-[#4F46E5]" data-id="element-621">+</span>
            </div>
            <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-xs mx-auto" data-id="element-622">
              {isFR ? 'Citoyens joignables en moins de 30 minutes lors d’une urgence' : 'Residents reachable in under 30 minutes during an emergency'}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Adoption metrics */}
      <motion.div {...reveal} transition={{
      duration: 0.7,
      delay: 0.15
    }} className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 border-t border-l border-[#E4E6EC] mb-20" data-id="element-623">
        {metrics.map(m => <div key={m.label} className="border-r border-b border-[#E4E6EC] p-6 md:p-8 text-center" data-id="element-624">
            <div className="font-serif text-3xl md:text-4xl text-[#121212] leading-none mb-3" data-id="element-625">
              {m.value}
            </div>
            <div className="font-sans text-xs text-[#121212]/55 uppercase tracking-wider leading-snug" data-id="element-626">
              {m.label}
            </div>
          </div>)}
      </motion.div>

      {/* Production screenshots */}
      <motion.div {...reveal} transition={{
      duration: 0.8,
      delay: 0.2
    }} className="max-w-6xl mx-auto" data-id="element-627">
        <div className="flex flex-col md:flex-row items-start justify-center gap-8 md:gap-10" data-id="element-628">
          <div className="w-full max-w-[300px] mx-auto md:mx-0 md:flex-shrink-0" data-id="element-629">
            <div className="flex items-center gap-2 mb-5 justify-center" data-id="element-630">
              <span className="inline-block w-3.5 h-3.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-631" />
              <span className="font-sans text-xs font-semibold tracking-[0.18em] uppercase text-[#121212]/55" data-id="element-632">
                {isFR ? 'Application citoyenne' : 'Citizen mobile app'}
              </span>
            </div>
            <img src={isFR ? 'https://cdn.magicpatterns.com/uploads/6ssDHDMHuDJ5EX6wAkS5z5/Capture_d’ecran,_le_2026-06-22_a_08.42.58.png' : 'https://cdn.magicpatterns.com/uploads/1pVBWf7FVvPhsQSXbKthD2/image.png'} alt={isFR ? 'Carte citoyenne numérique BCITI en production à Brossard' : 'BCITI digital citizen card live in Brossard'} className="w-full h-auto border border-[#E4E6EC] bg-white shadow-sm" data-id="element-633" />
          </div>

          <div className="w-full md:flex-1 md:min-w-0" data-id="element-634">
            <div className="flex items-center gap-2 mb-5 justify-center md:justify-start" data-id="element-635">
              <span className="inline-block w-3.5 h-3.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-636" />
              <span className="font-sans text-xs font-semibold tracking-[0.18em] uppercase text-[#121212]/55" data-id="element-637">
                {isFR ? 'Tableau de bord opérationnel' : 'Operational dashboard'}
              </span>
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/bRyXP6hkH7M9i1qxoHZ5jj/Capture_d’ecran,_le_2026-06-22_a_08.50.11.png" alt={isFR ? 'Tableau de bord des requêtes BCITI à Brossard' : 'BCITI requests analytics dashboard in Brossard'} className="w-full h-auto border border-[#E4E6EC] bg-white shadow-sm" data-id="element-638" />
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-14" data-id="element-639">
          <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-640">
            {isFR ? "Explorer l'étude de cas Brossard" : 'Explore the Brossard case study'}
            <ArrowRightIcon className="w-4 h-4" data-id="element-641" />
          </Link>
        </div>
      </motion.div>
    </section>;
}